import React from 'react';
import { css } from '@emotion/react';

import isMobile from '$/utils/isMobile';
import { CloseIcon } from '$/images/Icons';
import { SavedReplyFragment } from '$/GraphQL/schema/types';
import { markdownToText } from '$/shared/frontend/utils/MarkdownConverter';
import { RegularCirclePlusIcon, RegularRectangleListIcon } from '$/images/FontAwesomeIcons';

import { Truncate } from '$/components/Truncate/Truncate';
import { useWebinarContext } from '$/sections/Home/WebinarEditor/context';
import { usePermissionedNavigate } from '$/components/Router/usePermissionedNavigate';

import Button from '../ui/Button';
import { useSavedRepliesContext } from './SavedRepliesContext';
import { SavedReplyButtons } from './SavedRepliesTable/SavedReplyButtons';
import { useDropdownContext } from '../ui/Input/Basics/DropdownComponent';
import { SearchInputFilter } from '../ui/Input/Filters/SearchInputFilter';

interface SavedReplyListProps {
  onClickSavedReply: (savedReply: SavedReplyFragment) => void;
  onClose?: () => void;
  isRtl?: boolean;
}
export const SavedReplyList: React.FC<SavedReplyListProps> = ({
  onClickSavedReply,
  onClose,
  isRtl,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownContext = useDropdownContext();
  const [search, setSearch] = React.useState('');
  const webinarContext = useWebinarContext();
  const permissionedNavigate = usePermissionedNavigate();
  const { savedReplies, onAdd } = useSavedRepliesContext();

  const onInputChange = React.useCallback((v: string) => {
    setSearch(v);
  }, []);

  const onClickAdd = React.useCallback(() => {
    onAdd(webinarContext?.setId ? { availableIn: [webinarContext?.setId] } : undefined);
  }, [onAdd, webinarContext?.setId]);

  const onClickManage = React.useCallback(() => {
    permissionedNavigate('/home/setup/settings/saved-replies');
  }, [permissionedNavigate]);

  const savedRepliesFiltered = React.useMemo(() => {
    return savedReplies.filter(
      (savedReply) =>
        savedReply.name.toLowerCase().includes(search.toLowerCase()) ||
        savedReply.shortcut?.toLowerCase().includes(search.toLowerCase())
    );
  }, [savedReplies, search]);

  React.useEffect(() => {
    if (dropdownContext?.open) {
      setSearch('');
      inputRef.current?.focus();
    }
  }, [dropdownContext?.open]);

  return (
    <div
      ref={ref}
      tabIndex={0}
      css={css`
        padding: 1rem;
        width: ${isMobile ? 'calc(100vw - 2rem)' : '26rem'};
        max-width: ${isMobile ? 'calc(100vw - 2rem)' : '100%'};
      `}
    >
      <div
        className='flex flex-row justify-between items-center mb-1e'
        css={css`
          ${isRtl
            ? `
              button svg {
                margin-right: 0 !important;
                margin-left: 0.5rem;
              }
            `
            : ''}
        `}
      >
        <div className='flex flex-row items-center'>
          <Button.row onClick={onClickAdd} icon={RegularCirclePlusIcon}>
            Create new
          </Button.row>
          <Button.row
            className={`${isRtl ? 'mr-1e' : 'ml-1e'} hidden md:flex`}
            onClick={onClickManage}
            icon={RegularRectangleListIcon}
          >
            Manage replies
          </Button.row>
        </div>
        <CloseIcon className='w-1e h-1e' onClick={onClose} />
      </div>
      <SearchInputFilter
        className='mb-1e'
        placeholder='Search for a saved reply'
        isInputOpen
        onChange={onInputChange}
        value={search}
        inputRef={inputRef}
      />
      <div tabIndex={0} className='max-h-12e md:max-h-20e overflow-y-auto'>
        {savedRepliesFiltered.map((savedReply) => (
          <div
            key={savedReply.id}
            className='hover:bg-gray-200 cursor-pointer p-2 rounded relative'
            onClick={(e) => {
              console.debug('savedReply clicked', savedReply);
              e.preventDefault();
              onClickSavedReply(savedReply);
            }}
            css={css`
              .actions {
                display: none;
                opacity: 0;
                pointer-events: none;
                position: absolute;
                top: -0.25rem;
                padding: 0.5rem;
                right: 0;
                ${isRtl
                  ? `
                    right: auto;
                    left: 0;
                    button {
                      margin-right: 0.5rem;
                      margin-left: 0 !important;
                    }
                  `
                  : ''}
              }
              &:hover {
                .actions {
                  opacity: 1;
                  pointer-events: all;
                }
              }
              @media (min-width: 768px) {
                .actions {
                  display: block;
                }
              }
            `}
          >
            <div className='flex flex-row mb-1'>
              <div className='flex-1 text-lg font-semibold'>{savedReply.name}</div>
              <div className='text-gray-600'>#{savedReply.shortcut}</div>
              <div className='actions'>
                <SavedReplyButtons
                  css={css`
                    padding-right: 0;
                    button {
                      padding: 4px 6px;
                    }
                  `}
                  savedReply={savedReply}
                />
              </div>
            </div>
            <Truncate lines={1} type='div'>
              {markdownToText(savedReply.content)}
            </Truncate>
          </div>
        ))}
        {savedReplies.length === 0 && (
          <div className='p-2e mt-1e rounded-lg text-center border'>No saved replies</div>
        )}
      </div>
    </div>
  );
};
